import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { sediVisibili } from "@/lib/ambito";
import { useConfirm } from "@/components/ConfirmProvider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { errore, apriPipeline } from "./crmComuni";

// Sprint CRM-2A.1 — sede modificabile: le sedi su cui l'utente ha «visualizza», più «Tutte le sedi»
export const TUTTE = "_tutte";
export function useSediCrm(cid) {
  const { user } = useAuth() || {};
  const [sedi, setSedi] = useState([]);
  useEffect(() => { api.get(`/companies/${cid}/sites`).then((r) => setSedi(r.data)).catch(() => setSedi([])); }, [cid]);
  return sediVisibili(user, cid, sedi);
}

export function OpzioniSede({ sedi }) {
  return <>{!!sedi.length && <SelectItem value={TUTTE}>Tutte le sedi</SelectItem>}
    {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</>;
}

export function CambiaSede({ o, onFatto }) {
  const confirm = useConfirm();
  const sedi = useSediCrm(o.company_id);
  const attuale = o.tutte_le_sedi ? TUTTE : o.site_id || "";
  const scegli = async (v) => {
    if (v === attuale) return;
    if (!(await confirm({ variant: "approve", title: "Cambia sede", confirmLabel: "SI, cambia",
      message: "Chi non vede la nuova sede non vedrà più questa opportunità. Confermi?" }))) return;
    const tutte = v === TUTTE;
    try {
      const r = await api.put(`/crm/opportunita/${o.id}`, { site_id: tutte ? null : v, tutte_le_sedi: tutte,
        originali: { site_id: o.site_id ?? null, tutte_le_sedi: o.tutte_le_sedi === true } });
      if (r.data.conflitti?.length) toast.error("La sede è stata cambiata da un altro utente: ricarica la scheda");
      else toast.success("Sede cambiata");
    } catch (e) { toast.error(errore(e)); }
    onFatto();
  };
  return (
    <div><Label className="text-xs">Sede</Label>
      <Select value={attuale} onValueChange={scegli}>
        <SelectTrigger className="w-48" data-testid="crm-opp-sede"><SelectValue placeholder={o.sede_nome || "—"} /></SelectTrigger>
        <SelectContent><OpzioniSede sedi={sedi} /></SelectContent>
      </Select></div>
  );
}

// Sprint CRM-2A.1 — spostamento in un'altra pipeline: 1) pipeline e fase di arrivo, 2) avviso con le conseguenze
const ORIGINALI = ["pipeline_id", "fase_id", "stato", "titolare_id", "collaboratori"];
export function SpostaPipeline({ o, onChiudi }) {
  const nav = useNavigate();
  const [scelte, setScelte] = useState(null);
  const [pid, setPid] = useState("");
  const [fid, setFid] = useState("");
  const [passo, setPasso] = useState(1);
  const [nota, setNota] = useState("");  // Sprint CRM-9 — nota obbligatoria
  useEffect(() => {
    api.get(`/crm/opportunita/${o.id}/spostamento${pid ? `?pipeline_id=${pid}` : ""}`)
      .then((r) => { setScelte(r.data); if (pid) setFid(r.data.fase_proposta || ""); }).catch((e) => toast.error(errore(e)));
  }, [o.id, pid]);
  const nomeP = scelte?.pipeline?.find((p) => p.id === pid)?.nome;
  const nomeF = scelte?.fasi?.find((f) => f.id === fid)?.nome;
  const sposta = async () => {
    try {
      await api.post(`/crm/opportunita/${o.id}/pipeline`, { pipeline_id: pid, fase_id: fid, nota: nota.trim(),
        originali: Object.fromEntries(ORIGINALI.map((k) => [k, o[k] ?? null])) });
      toast.success("Opportunità spostata"); apriPipeline(o.company_id, pid); onChiudi(); nav(`/crm?pipeline=${pid}`);
    } catch (e) { toast.error(errore(e)); onChiudi(); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onChiudi(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crm-sposta-dialog">
        <DialogHeader><DialogTitle>Sposta in un'altra pipeline</DialogTitle></DialogHeader>
        {passo === 1 ? (
          <div className="space-y-3">
            {scelte && !scelte.pipeline.length && <div className="text-sm text-slate-500" data-testid="crm-sposta-nessuna">Nessun'altra pipeline disponibile.</div>}
            <div><Label>Pipeline di arrivo</Label>
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="crm-sposta-pipeline"><SelectValue placeholder="Scegli la pipeline" /></SelectTrigger>
                <SelectContent>{(scelte?.pipeline || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
              </Select></div>
            {pid && scelte?.fasi && <div><Label>Fase di arrivo</Label>
              <Select value={fid} onValueChange={setFid}>
                <SelectTrigger data-testid="crm-sposta-fase"><SelectValue placeholder="Scegli la fase" /></SelectTrigger>
                <SelectContent>{scelte.fasi.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}</SelectContent>
              </Select></div>}
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="crm-sposta-avviso">
            <p>L'opportunità passa in <b>{nomeP}</b>, fase <b>{nomeF}</b>. I giorni nella fase ripartono da oggi.</p>
            <p>Veicolo, contatto, note e interazioni restano.</p>
            {scelte.titolare_tolto && <p className="text-amber-800" data-testid="crm-sposta-titolare">Il titolare <b>{scelte.titolare_tolto}</b> non ha accesso
              alla nuova pipeline: viene tolto e l'opportunità diventa libera.</p>}
            {!!scelte.collaboratori_tolti?.length && <p className="text-amber-800" data-testid="crm-sposta-collaboratori">Collaboratori tolti perché senza
              accesso: <b>{scelte.collaboratori_tolti.join(", ")}</b>.</p>}
            <div><Label>Perché la sposti?</Label>
              <Textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Scrivi una nota" data-testid="crm-sposta-nota" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={passo === 1 ? onChiudi : () => setPasso(1)} data-testid="crm-sposta-indietro">{passo === 1 ? "Annulla" : "Indietro"}</Button>
          {passo === 1 ? <Button disabled={!pid || !fid} onClick={() => setPasso(2)} data-testid="crm-sposta-avanti">Avanti</Button>
            : <Button onClick={sposta} disabled={!nota.trim()} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-sposta-conferma">Conferma lo spostamento</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
